import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Star, 
  Crown, 
  Sparkles, 
  Check, 
  Heart,
  Users,
  Loader2,
  ExternalLink
} from 'lucide-react';


const TIER_ICONS = {
  supporter: { icon: Star, color: 'text-blue-400', bg: 'from-blue-500/20 to-blue-600/10' },
  superfan: { icon: Sparkles, color: 'text-purple-400', bg: 'from-purple-500/20 to-purple-600/10' },
  vip: { icon: Crown, color: 'text-amber-400', bg: 'from-amber-500/20 to-amber-600/10' }
};

function SubscriptionTierCard({ tier, isSubscribed, onSubscribe, loading }) {
  const tierStyle = TIER_ICONS[tier.tier_id] || TIER_ICONS.supporter;
  const Icon = tierStyle.icon;
  const priceFormatted = `$${(tier.price_cents / 100).toFixed(2)}`;

  return (
    <Card 
      className={`p-5 relative overflow-hidden transition-all hover:scale-[1.02] ${
        isSubscribed ? 'border-2 border-primary ring-2 ring-primary/20' : 'border border-border'
      }`}
      data-testid={`tier-card-${tier.tier_id}`}
    >
      {/* Background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${tierStyle.bg} opacity-50`} />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-black/30 flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${tierStyle.color}`} />
            </div>
            <div>
              <h3 className="font-bold">{tier.name}</h3>
              <p className="text-sm text-muted-foreground">{priceFormatted}/month</p>
            </div>
          </div>
          {isSubscribed && (
            <Badge className="bg-primary text-primary-foreground">
              <Check className="w-3 h-3 mr-1" /> Subscribed
            </Badge>
          )}
        </div>

        {/* Description */}
        {tier.description && (
          <p className="text-sm text-muted-foreground mb-4">{tier.description}</p>
        )}

        {/* Benefits */}
        <ul className="space-y-2 mb-4">
          {tier.benefits?.map((benefit, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tierStyle.color}`} />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>

        {/* Subscribe Button */}
        {!isSubscribed && (
          <Button 
            onClick={() => onSubscribe(tier)}
            className="w-full"
            disabled={loading}
            data-testid={`subscribe-btn-${tier.tier_id}`}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Heart className="w-4 h-4 mr-2" />
            )}
            Subscribe for {priceFormatted}/mo
          </Button>
        )}
      </div>
    </Card>
  );
}

export function ArtistSubscriptionSection({ artistId, artistName }) {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    fetchTiers();
    if (isAuthenticated) {
      checkSubscription();
    }
  }, [artistId, isAuthenticated]);

  const fetchTiers = async () => {
    try {
      const response = await fetch(`${API}/fan-subscriptions/tiers/${artistId}`);
      if (response.ok) {
        const data = await response.json();
        setTiers(data.tiers || []);
        setSubscriberCount(data.subscriber_count || 0);
      }
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const response = await fetch(`${API}/fan-subscriptions/check/${artistId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentSubscription(data.subscription);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleSubscribe = async (tier) => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }

    setSubscribing(tier.tier_id);
    try {
      const response = await fetch(`${API}/fan-subscriptions/subscribe/${artistId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ tier_id: tier.tier_id })
      });

      const result = await safeJson(response);

      if (result.ok && result.data) {
        if (result.data.checkout_url) {
          // Redirect to Stripe checkout
          window.location.href = result.data.checkout_url;
        } else {
          toast.success('Subscription started!');
          checkSubscription();
        }
      } else {
        const errorMessage = result.error || result.data?.detail || 'Failed to start subscription';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to start subscription');
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 bg-card/50 border border-border">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3" />
          <div className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (tiers.length === 0) {
    return null;
  }

  return (
    <Card className="p-6 bg-card/50 border border-border" data-testid="subscription-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-400" />
            Support {artistName}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Subscribe for exclusive content and perks
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="w-4 h-4" />
          <span>{subscriberCount} subscribers</span>
        </div>
      </div>

      {/* Subscription Tiers */}
      <div className="grid gap-4 md:grid-cols-3">
        {tiers.map((tier) => (
          <SubscriptionTierCard
            key={tier.tier_id}
            tier={tier}
            isSubscribed={currentSubscription?.tier_id === tier.tier_id}
            onSubscribe={handleSubscribe}
            loading={subscribing === tier.tier_id}
          />
        ))}
      </div>

      {/* Current Subscription Info */}
      {currentSubscription && (
        <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
          <p className="text-sm">
            <Check className="w-4 h-4 inline mr-1 text-primary" />
            You're subscribed to the <strong>{currentSubscription.tier_id}</strong> tier.
            Thank you for supporting {artistName}!
          </p>
        </div>
      )}

      {/* Login Prompt Dialog */}
      <Dialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign in to Subscribe</DialogTitle>
            <DialogDescription>
              Create an account or sign in to subscribe to {artistName}'s channel and unlock exclusive content.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => window.location.href = '/auth'} className="flex-1">
              Sign In
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" onClick={() => setShowLoginPrompt(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default ArtistSubscriptionSection;
